import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  FaCheckCircle,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaGlobe,
  FaStar,
  FaBriefcase,
  FaListUl,
  FaCommentDots,
  FaInfoCircle,
  FaPen,
  FaArrowRight,
} from "react-icons/fa";
import { Link } from "react-router-dom";

// Dummy vendor profile data
const vendor = {
  name: "Elite Events Group",
  avatar: "assets/img/vendor-avatar.png",
  rating: 4.9,
  reviews: 127,
  location: "Lagos, Nigeria",
  verified: true,
  contact: {
    phone: "07080972110",
    email: "eliteevents@gmail.com",
    website: "https://eliteevents.com",
  },
  bio: "We are Nigeria's leading event solutions provider, offering catering, decor, and entertainment for all occasions. Our team is passionate about making your event unforgettable.",
};

// Dummy business listings
const businessListings = [
  {
    id: 1,
    name: "Elite Catering",
    images: [
      "assets/img/product/hm9-pro-1.jpg",
      "assets/img/product/hm9-pro-2.jpg",
      "assets/img/product/hm9-pro-3.jpg",
    ],
    description:
      "Premium catering for weddings, parties, and corporate events. Custom menus and impeccable service.",
    location: "Lagos, Nigeria",
    featured: true,
    services: [
      { name: "Buffet Service", price: "₦200,000+" },
      { name: "Cocktail Bar", price: "₦80,000+" },
      { name: "Wait Staff", price: "₦50,000+" },
    ],
  },
  {
    id: 2,
    name: "Elite Decor",
    images: [
      "assets/img/product/hm9-pro-4.jpg",
      "assets/img/product/hm9-pro-7.jpg",
    ],
    description:
      "Transforming venues into magical event spaces with creative decor and floral arrangements.",
    location: "Abuja, Nigeria",
    featured: false,
    services: [
      { name: "Venue Decoration", price: "₦150,000+" },
      { name: "Floral Design", price: "₦60,000+" },
    ],
  },
  {
    id: 3,
    name: "Elite Entertainment",
    images: [
      "assets/img/product/hm9-pro-5.jpg",
      "assets/img/product/hm9-pro-6.jpg",
    ],
    description:
      "Live bands, DJs, and MCs to keep your guests entertained all night long.",
    location: "Port Harcourt, Nigeria",
    featured: false,
    services: [
      { name: "DJ Service", price: "₦100,000+" },
      { name: "Live Band", price: "₦250,000+" },
      { name: "MC", price: "₦70,000+" },
    ],
  },
];

// Dummy reviews
const reviews = [
  {
    name: "Ada O.",
    rating: 5,
    comment:
      "Absolutely amazing service! The food was delicious and the decor was stunning.",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
  },
  {
    name: "Chinedu K.",
    rating: 4,
    comment: "Professional team and great entertainment. Highly recommended!",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
  },
  {
    name: "Fatima S.",
    rating: 5,
    comment: "They made my wedding unforgettable. Thank you Elite Events!",
    avatar: "https://randomuser.me/api/portraits/women/65.jpg",
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <span>
      {[1, 2, 3, 4, 5].map((i) =>
        i <= Math.round(rating) ? (
          <FaStar key={i} color="#FFD700" style={{ marginRight: 2 }} />
        ) : (
          <FaStar key={i} color="#eee" style={{ marginRight: 2 }} />
        )
      )}
    </span>
  );
}

function ProductDetails() {
  const [activeTab, setActiveTab] = useState("listings");
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (tabBarRef.current) {
        setSticky(window.scrollY > tabBarRef.current.offsetTop + 40);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const tabs = [
    { key: "listings", label: "Business Listings", icon: <FaBriefcase /> },
    { key: "services", label: "Services", icon: <FaListUl /> },
    { key: "reviews", label: "Reviews", icon: <FaCommentDots /> },
    { key: "about", label: "About", icon: <FaInfoCircle /> },
  ];

  return (
    <>
      <div
        style={{
          background: "linear-gradient(120deg, #e3ecff 0%, #ffffff 100%)",
          minHeight: 320,
          padding: 0,
          position: "relative",
          paddingBottom: 64,
        }}
      >
        <div
          className="container"
          style={{
            display: "flex",
            gap: 48,
            alignItems: "flex-start",
            padding: "56px 0 0 0",
            flexWrap: "wrap",
            marginBottom: 64,
          }}
        >
          {/* Sidebar Profile Card */}
          <aside
            style={{
              flex: "0 0 340px",
              minWidth: 280,
              maxWidth: 380,
              position: "sticky",
              top: 32,
              alignSelf: "flex-start",
              zIndex: 10,
              marginBottom: 64,
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.55)",
                borderRadius: 32,
                boxShadow: "0 8px 32px rgba(108,99,255,0.13)",
                padding: 40,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0,
                position: "relative",
                backdropFilter: "blur(12px)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                animation: "fadeInUp 0.7s cubic-bezier(.4,2,.6,1)",
                marginBottom: 32,
              }}
            >
              <div style={{ position: "relative", marginBottom: 18 }}>
                {vendor.avatar ? (
                  <img
                    src={vendor.avatar}
                    alt={vendor.name}
                    style={{
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "5px solid #FFD700",
                      boxShadow:
                        "0 0 0 8px rgba(108,99,255,0.10), 0 8px 32px #eee",
                      background: "#fff",
                      zIndex: 1,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      background: "#FFD700",
                      color: "#6C63FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 54,
                      fontWeight: 900,
                      border: "5px solid #FFD700",
                      boxShadow:
                        "0 0 0 8px rgba(108,99,255,0.10), 0 8px 32px #eee",
                      zIndex: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    {vendor.name.charAt(0)}
                  </div>
                )}
                {vendor.verified && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 10,
                      right: 8,
                      background: "#fff",
                      borderRadius: "50%",
                      boxShadow: "0 2px 8px #eee",
                      padding: 2,
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaCheckCircle
                      color="#28a745"
                      title="Verified"
                      style={{
                        fontSize: 22,
                        animation: "pulse 1.5s infinite alternate",
                      }}
                    />
                  </span>
                )}
              </div>
              <h1
                style={{
                  fontWeight: 900,
                  fontSize: 28,
                  margin: 0,
                  letterSpacing: 0.5,
                  textAlign: "center",
                }}
              >
                {vendor.name}
              </h1>
              <div
                style={{
                  color: "#6C63FF",
                  fontWeight: 700,
                  fontSize: 15,
                  margin: "8px 0 10px 0",
                  textAlign: "center",
                }}
              >
                Event Vendor • Member since 2018
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  margin: "0 auto 10px auto",
                }}
              >
                <span style={{ color: "#FFD700", fontSize: 18 }}>
                  <StarRating rating={vendor.rating} />
                  {vendor.rating}
                </span>
                <span style={{ color: "#888", fontSize: 14 }}>
                  ({vendor.reviews})
                </span>
              </div>
              <div
                style={{
                  color: "#6C63FF",
                  fontWeight: 600,
                  fontSize: 15,
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  justifyContent: "center",
                }}
              >
                <FaMapMarkerAlt /> {vendor.location}
              </div>
              <div
                style={{
                  color: "#555",
                  fontSize: 15,
                  marginBottom: 14,
                  marginTop: 8,
                  lineHeight: 1.6,
                  textAlign: "center",
                }}
              >
                {vendor.bio}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginTop: 12,
                  width: "100%",
                }}
              >
                <a
                  href={`tel:${vendor.contact.phone}`}
                  style={{
                    color: "#6C63FF",
                    textDecoration: "none",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: "center",
                  }}
                >
                  <FaPhoneAlt /> {vendor.contact.phone}
                </a>
                <a
                  href={`mailto:${vendor.contact.email}`}
                  style={{
                    color: "#6C63FF",
                    textDecoration: "none",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: "center",
                  }}
                >
                  <FaEnvelope /> {vendor.contact.email}
                </a>
                <a
                  href={vendor.contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#6C63FF",
                    textDecoration: "none",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: "center",
                  }}
                >
                  <FaGlobe /> Website
                </a>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main style={{ flex: 1, minWidth: 0 }}>
            {/* Tabs Navigation */}
            <div
              ref={tabBarRef}
              style={{
                background: "#fff",
                borderBottom: "1px solid #eee",
                position: sticky ? "sticky" : "static",
                top: 0,
                zIndex: 20,
                boxShadow: sticky ? "0 2px 12px rgba(108,99,255,0.07)" : "none",
                transition: "box-shadow 0.2s",
                marginBottom: 0,
              }}
            >
              <div
                className="container"
                style={{
                  display: "flex",
                  gap: 32,
                  padding: "0 0 0 32px",
                  position: "relative",
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      background: "none",
                      color: activeTab === tab.key ? "#6C63FF" : "#888",
                      border: "none",
                      borderBottom:
                        activeTab === tab.key
                          ? "3px solid #6C63FF"
                          : "3px solid transparent",
                      borderRadius: 0,
                      padding: "18px 0 12px 0",
                      fontWeight: 700,
                      fontSize: 18,
                      cursor: "pointer",
                      marginRight: 32,
                      transition: "color 0.2s, border-bottom 0.2s",
                      outline: "none",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
                {/* Animated underline */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: `${
                      tabs.findIndex((t) => t.key === activeTab) * 160
                    }px`,
                    width: 140,
                    height: 3,
                    background: "#6C63FF",
                    borderRadius: 2,
                    transition: "left 0.3s cubic-bezier(.4,2,.6,1)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            {/* Tab Content */}
            <div
              style={{
                background: "#f8f8ff",
                minHeight: 400,
                padding: "48px 0",
              }}
            >
              <div className="container">
                {/* Business Listings Tab */}
                {activeTab === "listings" && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 40,
                      justifyContent: "flex-start",
                    }}
                  >
                    {businessListings.map((biz) => (
                      <div
                        key={biz.id}
                        style={{
                          background: "#fff",
                          borderRadius: 20,
                          boxShadow: "0 4px 24px #eee",
                          maxWidth: 370,
                          flex: "1 1 340px",
                          padding: 0,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          transition: "box-shadow 0.2s, transform 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform =
                            "translateY(-6px) scale(1.02)";
                          e.currentTarget.style.boxShadow =
                            "0 12px 32px rgba(108,99,255,0.13)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = "none";
                          e.currentTarget.style.boxShadow = "0 4px 24px #eee";
                        }}
                      >
                        {/* Featured badge */}
                        {biz.featured && (
                          <span
                            style={{
                              position: "absolute",
                              top: 18,
                              left: 18,
                              background: "#FFD700",
                              color: "#222",
                              fontWeight: 700,
                              fontSize: 13,
                              borderRadius: 8,
                              padding: "4px 14px",
                              zIndex: 2,
                              boxShadow: "0 2px 8px #eee",
                            }}
                          >
                            ★ Featured
                          </span>
                        )}
                        {/* Business Images Carousel */}
                        <Swiper
                          modules={[Pagination, Autoplay]}
                          pagination={{ clickable: true }}
                          autoplay={{ delay: 3500 }}
                          loop
                          style={{
                            width: "100%",
                            height: 200,
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                          }}
                        >
                          {biz.images.map((img, idx) => (
                            <SwiperSlide key={idx}>
                              <img
                                src={img}
                                alt={biz.name}
                                style={{
                                  width: "100%",
                                  height: 200,
                                  objectFit: "cover",
                                  borderTopLeftRadius: 20,
                                  borderTopRightRadius: 20,
                                }}
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                        <div
                          style={{
                            padding: 28,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <h3
                            style={{
                              color: "#222",
                              fontWeight: 700,
                              marginBottom: 8,
                            }}
                          >
                            {biz.name}
                          </h3>
                          <div
                            style={{
                              color: "#6C63FF",
                              fontWeight: 600,
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <FaMapMarkerAlt />
                            {biz.location}
                          </div>
                          <div
                            style={{
                              color: "#555",
                              fontSize: 15,
                              marginBottom: 12,
                            }}
                          >
                            {biz.description}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              marginBottom: 16,
                            }}
                          >
                            {biz.services.map((s, i) => (
                              <span
                                key={i}
                                style={{
                                  background: "#f8f8ff",
                                  color: "#6C63FF",
                                  borderRadius: 6,
                                  padding: "4px 12px",
                                  fontWeight: 600,
                                  fontSize: 14,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <FaCheckCircle
                                  color="#28a745"
                                  style={{ fontSize: 13 }}
                                />{" "}
                                {s.name}
                              </span>
                            ))}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              marginTop: "auto",
                            }}
                          >
                            <Link
                              to={`/business-listing/${biz.id}`}
                              state={{ biz }}
                              style={{
                                background: "#6C63FF",
                                color: "#fff",
                                borderRadius: 8,
                                padding: "12px 0",
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: 16,
                                textDecoration: "none",
                                display: "block",
                                boxShadow: "0 2px 8px #eee",
                                transition: "background 0.2s",
                                letterSpacing: 1,
                                flex: 1,
                              }}
                            >
                              View Details
                            </Link>
                            <a
                              href="/subscription"
                              style={{
                                background: "#FFD700",
                                color: "#222",
                                borderRadius: 8,
                                padding: "12px 0",
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: 16,
                                textDecoration: "none",
                                display: "block",
                                boxShadow: "0 2px 8px #eee",
                                transition: "background 0.2s",
                                letterSpacing: 1,
                                flex: 1,
                                marginLeft: 0,
                              }}
                            >
                              Book Now{" "}
                              <FaArrowRight style={{ marginLeft: 6 }} />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Services Tab */}
                {activeTab === "services" && (
                  <div>
                    {businessListings.map((biz) => (
                      <div key={biz.id} style={{ marginBottom: 40 }}>
                        <h3
                          style={{
                            color: "#222",
                            fontWeight: 700,
                            marginBottom: 12,
                          }}
                        >
                          {biz.name}
                        </h3>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 20 }}
                        >
                          {biz.services.map((s, i) => (
                            <div
                              key={i}
                              style={{
                                background: "#fff",
                                borderRadius: 10,
                                boxShadow: "0 2px 8px #eee",
                                padding: "18px 28px",
                                minWidth: 180,
                                fontWeight: 600,
                                color: "#6C63FF",
                                fontSize: 16,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 6,
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <FaCheckCircle color="#28a745" /> {s.name}
                              </span>
                              <span
                                style={{
                                  color: "#888",
                                  fontWeight: 400,
                                  fontSize: 14,
                                }}
                              >
                                {s.price}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reviews Tab */}
                {activeTab === "reviews" && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: 32,
                      }}
                    >
                      <button
                        style={{
                          background: "#6C63FF",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          padding: "10px 24px",
                          fontWeight: 700,
                          fontSize: 16,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          boxShadow: "0 2px 8px #eee",
                          transition: "background 0.2s",
                        }}
                      >
                        <FaPen /> Write a Review
                      </button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 40 }}>
                      {reviews.map((r, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: "#fff",
                            borderRadius: 20,
                            boxShadow: "0 4px 24px #eee",
                            padding: 36,
                            maxWidth: 360,
                            flex: "1 1 300px",
                            textAlign: "center",
                            transition: "box-shadow 0.2s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.boxShadow =
                              "0 12px 32px rgba(108,99,255,0.13)")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.boxShadow =
                              "0 4px 24px #eee")
                          }
                        >
                          <img
                            src={r.avatar}
                            alt={r.name}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              marginBottom: 16,
                              objectFit: "cover",
                              border: "3px solid #eee",
                            }}
                          />
                          <div
                            style={{
                              color: "#444",
                              fontWeight: 600,
                              fontSize: 18,
                              marginBottom: 8,
                            }}
                          >
                            {r.name}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <StarRating rating={r.rating} />
                          </div>
                          <div
                            style={{
                              color: "#888",
                              fontSize: 15,
                              fontStyle: "italic",
                            }}
                          >
                            &ldquo;{r.comment}&rdquo;
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* About Tab */}
                {activeTab === "about" && (
                  <div
                    style={{
                      maxWidth: 700,
                      margin: "0 auto",
                      color: "#444",
                      fontSize: 18,
                      background: "#fff",
                      borderRadius: 16,
                      boxShadow: "0 2px 8px #eee",
                      padding: 40,
                    }}
                  >
                    <h3
                      style={{
                        color: "#222",
                        fontWeight: 700,
                        marginBottom: 16,
                      }}
                    >
                      About {vendor.name}
                    </h3>
                    <p>{vendor.bio}</p>
                    <div style={{ marginTop: 24 }}>
                      <strong>Contact:</strong>
                      <div style={{ marginTop: 8 }}>
                        <span
                          style={{
                            marginRight: 16,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <FaPhoneAlt /> {vendor.contact.phone}
                        </span>
                        <span
                          style={{
                            marginRight: 16,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <FaEnvelope /> {vendor.contact.email}
                        </span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <FaGlobe />{" "}
                          <a
                            href={vendor.contact.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#6C63FF" }}
                          >
                            {vendor.contact.website}
                          </a>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: none; }
        }
      `}</style>
      {/* Animated pulse for verified badge */}
      <style>{`
        @keyframes pulse {
          0% { filter: drop-shadow(0 0 0 #28a745); }
          100% { filter: drop-shadow(0 0 8px #28a745); }
        }
      `}</style>
    </>
  );
}

export default ProductDetails;
